import { useState } from "react";
import { CheckCircle2, Circle, Copy, ExternalLink, Link2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { adminAgent, agentError, copyText } from "@/lib/agent";
import { AgentButton, Badge, Field, formatDate } from "./AgentUI";

const origin = () => window.location.origin.replace(/\/$/, "");

export const WhatsAppConfig = ({ data, load, setError }) => {
  const [verifying, setVerifying] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const verification = data.verification || {};
  const current = verification.callback_url || "";
  const target = origin() + "/api/webhooks/whatsapp";
  const pointsHere = current === target;
  const verify = async () => {
    setVerifying(true); setError("");
    try { await api.post(adminAgent + "/whatsapp/verify"); await load(); }
    catch (e) { setError(agentError(e)); }
    finally { setVerifying(false); }
  };
  const connect = async () => {
    setConnecting(true); setError(""); setConfirm(false);
    try {
      const { data: res } = await api.post(adminAgent + "/whatsapp/webhook", { base_url: origin() });
      await load();
      if (res.webhook_configured) toast.success("Twilio ya envía los mensajes de WhatsApp a este servidor");
      else toast.message("Twilio aceptó el cambio; vuelve a verificar en unos segundos para confirmarlo");
    } catch (e) { setError(agentError(e)); }
    finally { setConnecting(false); }
  };
  const copy = async value => {
    try { await copyText(value); toast.success("Dirección copiada"); }
    catch { toast.error("No se pudo copiar"); }
  };
  return <section className="ag-whatsapp-config">
    <h2>Configuración del canal</h2>
    <div className="ag-env-list">{data.fields.map(f => <div key={f.key} data-testid={`whatsapp-env-${f.key.toLowerCase()}`}>
      {f.present ? <CheckCircle2 size={17} className="ag-green-text" /> : <Circle size={17} />}
      <code>{f.key}</code><span>{f.present ? "Presente" : "Pendiente"}</span>
    </div>)}</div>
    <div className="ag-whatsapp-verification">
      <p data-testid="whatsapp-sender">Remitente: {data.phone || "Pendiente"}</p>
      <Badge id="whatsapp-account-status" tone={verification.account_verified ? "green" : "amber"}>Cuenta: {verification.account_verified ? "Verificada" : "Por verificar"}</Badge>
      <Badge id="whatsapp-sender-status" tone={verification.sender_verified ? "green" : "amber"}>Número: {verification.sender_verified ? "Disponible" : "Por verificar"}</Badge>
      <Badge id="whatsapp-webhook-status" tone={pointsHere ? "green" : "amber"}>Recepción: {pointsHere ? "En este servidor" : current ? "En otro servidor" : "Pendiente"}</Badge>
      {verification.checked_at && <small data-testid="whatsapp-verified-at">Última consulta: {formatDate(verification.checked_at)}</small>}
    </div>
    <AgentButton secondary onClick={verify} disabled={!data.configured || verifying} data-testid="whatsapp-verify"><ShieldCheck size={16} />{verifying ? "Verificando…" : "Verificar cuenta y número"}</AgentButton>
    <div className="ag-whatsapp-callback">
      <Field label="Twilio envía los mensajes a" id="whatsapp-current-callback" value={current || "Sin registrar · pulsa Verificar para consultar"} readOnly />
    </div>
    <div className="ag-whatsapp-callback">
      <Field label="Dirección de recepción de este servidor · POST" id="whatsapp-callback" value={target} readOnly />
      <AgentButton secondary data-testid="whatsapp-copy-callback" onClick={() => copy(target)}><Copy size={15} />Copiar recepción</AgentButton>
    </div>
    <div className="ag-whatsapp-callback">
      <Field label="Dirección de estados de este servidor · POST" id="whatsapp-status-callback" value={origin() + "/api/webhooks/whatsapp/status"} readOnly />
      <AgentButton secondary data-testid="whatsapp-copy-status-callback" onClick={() => copy(origin() + "/api/webhooks/whatsapp/status")}><Copy size={15} />Copiar estados</AgentButton>
    </div>
    {!confirm
      ? <AgentButton onClick={() => setConfirm(true)} disabled={!data.configured || connecting || pointsHere} data-testid="whatsapp-connect-here"><Link2 size={16} />{pointsHere ? "Recepción conectada a este servidor" : "Conectar recepción a este servidor"}</AgentButton>
      : <div className="ag-whatsapp-confirm" data-testid="whatsapp-connect-confirm">
        <p>Twilio dejará de enviar los mensajes a <code>{current || "la dirección actual"}</code> y los enviará a <code>{target}</code>. Es un cambio real en tu cuenta de Twilio.</p>
        <div className="ag-actions">
          <AgentButton onClick={connect} disabled={connecting} data-testid="whatsapp-connect-accept">{connecting ? "Conectando…" : "Sí, conectar aquí"}</AgentButton>
          <AgentButton secondary onClick={() => setConfirm(false)} disabled={connecting} data-testid="whatsapp-connect-cancel">Cancelar</AgentButton>
        </div>
      </div>}
    <ol className="ag-setup-steps" data-testid="whatsapp-setup-steps">
      <li>Pulsa “Verificar cuenta y número” para consultar a qué servidor envía Twilio los mensajes.</li>
      <li>Si apunta a otro servidor, usa “Conectar recepción a este servidor” o registra las direcciones manualmente en Twilio: Messaging → Senders → WhatsApp Senders.</li>
      <li>Para iniciar contacto, utiliza el Content SID de una plantilla aprobada en Content Template Builder.</li>
    </ol>
    <a href="https://www.twilio.com/docs/whatsapp/api" target="_blank" rel="noreferrer" className="ag-text-link" data-testid="whatsapp-twilio-docs">Guía de WhatsApp en Twilio<ExternalLink size={15} /></a>
  </section>;
};
